import React, { useState, useEffect } from 'react';
import { getProvinces, getWardsByProvince } from '../../services/provinceService';
import { submitFormData } from '../../services/formService';
import { uploadMultipleImages, uploadMultipleVideos } from '../../services/cloudinaryService';
import Swal from 'sweetalert2';

const FormSection = () => {
  // State cho form data
  const [formData, setFormData] = useState({
    sharedName: '',
    sharedPhone: '',
    recipientName: '',
    recipientPhone: '',
    province: '',
    ward: '',
    street: '',
    story: '',
  });

  // State cho danh sách địa phương
  const [provinces, setProvinces] = useState([]);
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(false);

  // State cho file uploads
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    images: { completed: 0, total: 0, percentage: 0, currentFile: '' },
    videos: { completed: 0, total: 0, percentage: 0, currentFile: '' },
  });

  // Load danh sách tỉnh/thành phố khi component mount
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        setLoading(true);
        const data = await getProvinces();
        setProvinces(data);
      } catch (error) {
        console.error('Error loading provinces:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProvinces();
  }, []);

  // Load danh sách phường/xã khi chọn tỉnh
  const handleProvinceChange = async (provinceCode) => {
    setFormData((prev) => ({ ...prev, province: provinceCode, ward: '' }));
    setWards([]);

    if (provinceCode) {
      try {
        setLoading(true);
        const data = await getWardsByProvince(provinceCode);
        setWards(data);
      } catch (error) {
        console.error('Error loading wards:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImages((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            file,
            preview: event.target.result,
            name: file.name,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle video upload
  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const videoFiles = files.filter((file) => file.type.startsWith('video/'));

    videoFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedVideos((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            file,
            preview: event.target.result,
            name: file.name,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove uploaded file
  const removeFile = (id, type) => {
    if (type === 'image') {
      setUploadedImages((prev) => prev.filter((img) => img.id !== id));
    } else {
      setUploadedVideos((prev) => prev.filter((vid) => vid.id !== id));
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsUploading(true);

    try {
      // Upload images và videos lên Cloudinary trước (đa luồng)
      let imageUrls = [];
      let videoUrls = [];

      // Upload images với progress tracking
      if (uploadedImages.length > 0) {
        const imageFiles = uploadedImages.map((img) => img.file);
        const imageResult = await uploadMultipleImages(
          imageFiles,
          'gk88-charity/images',
          (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              images: {
                completed: progress.completed,
                total: progress.total,
                percentage: progress.percentage,
                currentFile: progress.currentFile,
              },
            }));
          },
        );

        if (!imageResult.success) {
          throw new Error(`Không thể upload ${imageResult.failedFiles} hình ảnh`);
        }

        imageUrls = imageResult.urls;
      }

      // Upload videos với progress tracking
      if (uploadedVideos.length > 0) {
        const videoFiles = uploadedVideos.map((vid) => vid.file);
        const videoResult = await uploadMultipleVideos(
          videoFiles,
          'gk88-charity/videos',
          (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              videos: {
                completed: progress.completed,
                total: progress.total,
                percentage: progress.percentage,
                currentFile: progress.currentFile,
              },
            }));
          },
        );

        if (!videoResult.success) {
          throw new Error(`Không thể upload ${videoResult.failedFiles} video`);
        }

        videoUrls = videoResult.urls;
      }

      setIsUploading(false);

      // Tìm tên tỉnh và phường/xã từ code
      const selectedProvince = provinces.find((p) => p.code.toString() === formData.province);
      const selectedWard = wards.find((w) => w.code.toString() === formData.ward);

      // Chuẩn bị dữ liệu theo format API
      const submitData = {
        ...formData,
        provinceName: selectedProvince?.name || '',
        wardName: selectedWard?.name || '',
        imageUrls: imageUrls, // URLs từ Cloudinary
        videoUrls: videoUrls, // URLs từ Cloudinary
      };

      // Gửi dữ liệu lên API
      const result = await submitFormData(submitData);

      if (result.success) {
        // Hiển thị thông báo thành công
        await Swal.fire({
          icon: 'success',
          title: 'Thành công!',
          text: result.message,
          confirmButtonText: 'OK',
          confirmButtonColor: '#fe6601',
        });

        // Reset form sau khi gửi thành công
        setFormData({
          sharedName: '',
          sharedPhone: '',
          recipientName: '',
          recipientPhone: '',
          province: '',
          ward: '',
          street: '',
          story: '',
        });
        setUploadedImages([]);
        setUploadedVideos([]);
        setWards([]);
      } else {
        // Hiển thị thông báo lỗi
        await Swal.fire({
          icon: 'error',
          title: 'Lỗi!',
          text: result.message,
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Hiển thị thông báo lỗi
      await Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Có lỗi xảy ra. Vui lòng liên hệ CSKH',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setUploadProgress({
        images: { completed: 0, total: 0, percentage: 0, currentFile: '' },
        videos: { completed: 0, total: 0, percentage: 0, currentFile: '' },
      });
    }
  };

  return (
    <div className="container mx-auto rounded py-2 bg-transparent lg:block p-4 sm:p-5">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[1200px] mx-auto bg-white p-4 lg:p-8 rounded-lg border-2 border-dashed border-[#fe6601]"
      >
        <h1 className="text-3xl font-bold lg:text-center text-[#fe6601] uppercase mb-3 md:mb-8">
          Phiếu chia sẻ
        </h1>
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:mb-8 w-full lg:w-1/2">
            <div className="lg:mb-8 mb-4 w-full">
              <h2 className="text-lg lg:text-xl text-[#fe6601] font-semibold mb-2">
                Thông tin người chia sẻ
              </h2>
              <div className="grid md:grid-cols-2 gap-2 lg:gap-4">
                <div>
                  <label
                    htmlFor="sharedName"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Họ và tên
                  </label>
                  <input
                    id="sharedName"
                    name="sharedName"
                    required
                    placeholder="Nhập họ và tên"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    type="text"
                    value={formData.sharedName}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="sharedPhone"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Số điện thoại liên hệ
                  </label>
                  <input
                    id="sharedPhone"
                    name="sharedPhone"
                    required
                    placeholder="Nhập số điện thoại"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    pattern="^(0|84)\d{9,10}$"
                    title="Số điện thoại phải bắt đầu bằng 0 hoặc 84 và có 10-11 chữ số"
                    type="tel"
                    value={formData.sharedPhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="lg:mb-8 w-full">
              <h2 className="text-lg lg:text-xl text-[#fe6601] font-semibold mb-2">
                Thông tin người cần giúp đỡ
              </h2>
              <div className="grid md:grid-cols-2 gap-2 lg:gap-4">
                <div>
                  <label
                    htmlFor="recipientName"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Họ và tên người cần giúp đỡ
                  </label>
                  <input
                    id="recipientName"
                    name="recipientName"
                    required
                    placeholder="Nhập tên người cần giúp đỡ"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    type="text"
                    value={formData.recipientName}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="recipientPhone"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Số điện thoại liên hệ
                  </label>
                  <input
                    id="recipientPhone"
                    name="recipientPhone"
                    required
                    placeholder="Nhập số điện thoại liên hệ"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    pattern="^(0|84)\d{9,10}$"
                    title="Số điện thoại phải bắt đầu bằng 0 hoặc 84 và có 10-11 chữ số"
                    type="tel"
                    value={formData.recipientPhone}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="province"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Tỉnh/Thành phố
                  </label>
                  <select
                    id="province"
                    name="province"
                    required
                    value={formData.province}
                    onChange={(e) => handleProvinceChange(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    disabled={loading}
                  >
                    <option value="">Tỉnh - Thành phố</option>
                    {provinces.map((province) => (
                      <option key={province.code} value={province.code}>
                        {province.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ward" className="block mb-2 text-sm font-medium text-gray-900">
                    Phường/Xã
                  </label>
                  <select
                    id="ward"
                    name="ward"
                    required
                    value={formData.ward}
                    onChange={handleInputChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    disabled={!formData.province || loading}
                  >
                    <option value="">Chọn phường/xã</option>
                    {wards.map((ward) => (
                      <option key={ward.code} value={ward.code}>
                        {ward.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="street" className="block mb-2 text-sm font-medium text-gray-900">
                    Số nhà, tên đường
                  </label>
                  <input
                    id="street"
                    name="street"
                    required
                    placeholder="Nhập số nhà, tên đường"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full lg:max-w-[300px] p-2.5"
                    type="text"
                    value={formData.street}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 w-full">
            <div className="lg:mb-8 mb-4 w-full">
              <h2 className="text-lg lg:text-xl text-[#fe6601] font-semibold mb-2">
                Nội dung câu chuyện
              </h2>
              <label htmlFor="story" className="block mb-2 text-sm font-medium text-gray-900">
                Chi tiết câu chuyện
              </label>
              <textarea
                id="story"
                name="story"
                rows="5"
                required
                placeholder="Vui lòng chia sẻ chi tiết về người cần giúp đỡ, bao gồm lý do và tình trạng hiện tại"
                className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                value={formData.story}
                onChange={handleInputChange}
              ></textarea>
            </div>
            <div className="lg:mb-8 mb-4 w-full">
              <h2 className="text-lg text-[#fe6601] font-semibold mb-2">
                Hình ảnh và video minh chứng
              </h2>
              <p className="text-xs lg:text-sm text-[#5F5F5F] font-normal mb-4">
                * Vui lòng đính kèm hình ảnh hoặc video minh chứng cho câu chuyện để tăng tính
                thuyết phục và giúp chúng tôi hỗ trợ bạn tốt hơn. ( Kích thước nhỏ hơn 16MB)
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                  <div className="w-full sm:w-auto flex flex-row gap-3 sm:gap-4">
                    <div>
                      <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 relative border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100">
                        <input
                          id="images"
                          accept="image/*"
                          multiple
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          type="file"
                          onChange={handleImageUpload}
                        />
                        <div className="text-center">
                          <svg
                            className="mx-auto h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                          <p className="mt-1 text-xs sm:text-sm text-gray-600">Hình ảnh</p>
                        </div>
                      </div>
                    </div>
                    <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 relative border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100">
                      <input
                        id="videos"
                        accept="video/*"
                        multiple
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        type="file"
                        onChange={handleVideoUpload}
                      />
                      <div className="text-center">
                        <svg
                          className="mx-auto h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></path>
                        </svg>
                        <p className="mt-1 text-xs sm:text-sm text-gray-600">Video</p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting || isUploading}
                    className={`text-sm sm:text-md cursor-pointer text-white font-bold px-3 py-2 sm:px-4 sm:py-3 rounded-xl transition-colors w-full sm:w-auto ${
                      isSubmitting || isUploading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-[#fe6601] hover:bg-[#e55a00]'
                    }`}
                  >
                    {isUploading
                      ? `Đang upload... ${
                          uploadProgress.images.percentage + uploadProgress.videos.percentage > 0
                            ? `(${
                                uploadProgress.images.percentage + uploadProgress.videos.percentage
                              }%)`
                            : ''
                        }`
                      : isSubmitting
                      ? 'Đang gửi...'
                      : 'Gửi Thông Tin'}
                  </button>
                </div>
              </div>

              {/* Upload Progress */}
              {isUploading &&
                (uploadProgress.images.total > 0 || uploadProgress.videos.total > 0) && (
                  <div className="mt-4 p-3 lg:p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-xs lg:text-sm font-medium text-blue-800 mb-2">
                      Tiến trình upload:
                    </h4>

                    {/* Images Progress */}
                    {uploadProgress.images.total > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-blue-700 mb-1">
                          <span className="truncate mr-2">
                            Hình ảnh: {uploadProgress.images.completed}/
                            {uploadProgress.images.total}
                          </span>
                          <span className="font-semibold">{uploadProgress.images.percentage}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-1.5 lg:h-2">
                          <div
                            className="bg-blue-600 h-1.5 lg:h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress.images.percentage}%` }}
                          ></div>
                        </div>
                        {uploadProgress.images.currentFile && (
                          <p className="text-xs text-blue-600 mt-1 truncate">
                            Đang upload: {uploadProgress.images.currentFile}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Videos Progress */}
                    {uploadProgress.videos.total > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-blue-700 mb-1">
                          <span className="truncate mr-2">
                            Video: {uploadProgress.videos.completed}/{uploadProgress.videos.total}
                          </span>
                          <span className="font-semibold">{uploadProgress.videos.percentage}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-1.5 lg:h-2">
                          <div
                            className="bg-blue-600 h-1.5 lg:h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress.videos.percentage}%` }}
                          ></div>
                        </div>
                        {uploadProgress.videos.currentFile && (
                          <p className="text-xs text-blue-600 mt-1 truncate">
                            Đang upload: {uploadProgress.videos.currentFile}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

              {/* Preview uploaded files */}
              {(uploadedImages.length > 0 || uploadedVideos.length > 0) && (
                <div className="mt-4 lg:mt-6">
                  {/* Preview Images */}
                  {uploadedImages.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm lg:text-md font-medium text-gray-700 mb-2">
                        Hình ảnh đã tải lên:
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
                        {uploadedImages.map((image) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.preview}
                              alt={image.name}
                              className="w-full h-24 sm:h-32 lg:h-36 object-cover rounded-lg border border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => removeFile(image.id, 'image')}
                                className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 lg:p-1.5 hover:bg-red-600 transition-all duration-200"
                                title="Xóa ảnh"
                              >
                                <svg
                                  className="w-3 h-3 lg:w-4 lg:h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate" title={image.name}>
                              {image.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview Videos */}
                  {uploadedVideos.length > 0 && (
                    <div>
                      <h4 className="text-sm lg:text-md font-medium text-gray-700 mb-2">
                        Video đã tải lên:
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                        {uploadedVideos.map((video) => (
                          <div key={video.id} className="relative group">
                            <video
                              src={video.preview}
                              controls
                              className="w-full h-32 sm:h-40 lg:h-48 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeFile(video.id, 'video')}
                              className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-red-500 text-white rounded-full p-1 lg:p-1.5 hover:bg-red-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                              title="Xóa video"
                            >
                              <svg
                                className="w-3 h-3 lg:w-4 lg:h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate" title={video.name}>
                              {video.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FormSection;
